import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * System health / observability — DB connection pools (HikariCP per master +
 * tenant DB) and the webhook consumer queue. Backend: PoolMonitoringController
 * (/admin/pools) + WebhookMonitorController (/api/webhook).
 */
export interface PoolStats {
  totalConnections: number;
  activeConnections: number;
  idleConnections: number;
  threadsAwaiting: number;
  maxPoolSize: number;
}
export interface PoolHealth {
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | string;
  message: string;
  utilizationPercent: number;
}
export interface WebhookStats { waiting?: number; active?: number; completed?: number; failed?: number; [k: string]: number | undefined; }

export interface AsteriskEndpoint { id: string; tech?: string; resource?: string; state?: number; state_text?: string; channels?: number; }
export interface AsteriskMetrics {
  reachable: boolean;
  error?: string;
  version?: string;
  uptime_seconds?: number;
  channels?: number;
  calls_current?: number;
  calls_total?: number;
  bridges?: number;
  endpoints_count?: number;
  endpoints?: AsteriskEndpoint[];
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const systemHealthService = {
  poolStats: async (): Promise<ApiResponse<Record<string, PoolStats>>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.POOL_STATS)).data; }
    catch (e: any) { return err(e, 'Failed to load pool stats'); }
  },
  poolHealth: async (): Promise<ApiResponse<Record<string, PoolHealth>>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.POOL_HEALTH)).data; }
    catch (e: any) { return err(e, 'Failed to load pool health'); }
  },
  // WebhookMonitorController returns the raw stats map (not wrapped in ApiResponse).
  webhookStats: async (): Promise<WebhookStats | null> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.WEBHOOK_STATS)).data; }
    catch { return null; }
  },
  // Asterisk res_prometheus /metrics scrape — trunk SIP health + live channels.
  asteriskMetrics: async (url?: string): Promise<ApiResponse<AsteriskMetrics>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.ASTERISK_METRICS, { params: url ? { url } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to scrape Asterisk metrics'); }
  },
};
